import { normalizeOperatorName, OTHER_OPERATOR_SLUG, type StationsFeature } from '@ev/shared'
import { foldText, searchTokens, tokenScore } from './fold'

/**
 * The networks inside "Inni operatorzy", indexed so they can be searched for.
 *
 * The registry tracks the brands a driver would name and folds everything else
 * onto `other` - 9 % of the country as one bucket. That is the right shape for a
 * filter list nobody can scroll, and the wrong shape for somebody who holds a
 * roaming card that works at exactly one of those networks: typing its name used
 * to return nothing, because after the fold the map no longer knew the name.
 *
 * `stations.geojson` now carries the upstream name on those features, so the
 * bucket can be opened from here. This module is the second half of the same
 * split the registry does: identity is the *folded* name, so `Threeforce B.V.`,
 * `THREEFORCE BV` and `Threeforce` are one network with one filter value, while
 * the spelling shown is whichever one the most sites use.
 *
 * The index is built once when the artifact lands, like the site directory
 * beside it. There are a few hundred of these against 9 000 sites, so a keystroke
 * scores a few hundred short strings rather than folding the country again.
 */

export interface UntrackedOperator {
  /** {@link operatorKey} of the name: the identity, and the filter value. */
  readonly key: string
  /** The spelling to show, from whichever variant covers the most sites. */
  readonly name: string
  /** Sites carrying this name in the whole artifact, before any filtering. */
  readonly siteCount: number
  /** Folded, for ranking. */
  readonly folded: string
}

/**
 * Fold a raw operator name to the value the filter stores.
 *
 * The registry's own fold - diacritics, case, legal forms, punctuation - so the
 * one an untracked network gets is the one a tracked brand would have got. Cached
 * because the filter predicate asks for it per feature per pass, and the answer
 * for `Threeforce B.V.` never changes.
 */
const KEYS = new Map<string, string>()

export const operatorKey = (name: string): string => {
  const cached = KEYS.get(name)
  if (cached !== undefined) return cached

  const key = normalizeOperatorName(name)
  KEYS.set(name, key)
  return key
}

/** How many untracked networks the search box offers below the tracked brands. */
export const UNTRACKED_SUGGESTION_LIMIT = 5

/**
 * Group the `other` bucket by folded name, biggest network first.
 *
 * Ties break on name so the order is stable between builds; a name that folds to
 * nothing at all - a source publishing punctuation - is dropped rather than
 * offered as a blank row.
 */
export const buildUntrackedOperators = (
  features: readonly StationsFeature[],
): readonly UntrackedOperator[] => {
  const groups = new Map<string, { readonly key: string; spellings: Map<string, number> }>()

  features.forEach(({ properties }) => {
    if (properties.op !== OTHER_OPERATOR_SLUG) return
    const name = properties.opn?.trim() ?? ''
    if (name === '') return

    const key = operatorKey(name)
    if (key === '') return

    const group = groups.get(key) ?? { key, spellings: new Map<string, number>() }
    group.spellings.set(name, (group.spellings.get(name) ?? 0) + 1)
    groups.set(key, group)
  })

  const operators = [...groups.values()].map(({ key, spellings }) => {
    let name = ''
    let best = 0
    let siteCount = 0
    spellings.forEach((count, spelling) => {
      siteCount += count
      // Ties break alphabetically rather than on insertion order, so the label
      // does not depend on where in the artifact the site happened to sit.
      if (count > best || (count === best && spelling.localeCompare(name) < 0)) {
        best = count
        name = spelling
      }
    })
    return { key, name, siteCount, folded: foldText(`${name} ${key}`) }
  })

  operators.sort((a, b) => b.siteCount - a.siteCount || a.name.localeCompare(b.name))
  return operators
}

/**
 * Untracked networks matching the query, best match first.
 *
 * Same all-tokens-must-match rule as the brand and site searches, so "three
 * force" narrows rather than widening. Where two score equally the bigger
 * network wins, because a search that cannot tell them apart should offer the
 * one more of the country will have meant.
 */
export const searchUntrackedOperators = (
  operators: readonly UntrackedOperator[],
  query: string,
  limit: number = UNTRACKED_SUGGESTION_LIMIT,
): UntrackedOperator[] => {
  const tokens = searchTokens(query)
  if (tokens.length === 0) return []

  const hits: { operator: UntrackedOperator; score: number }[] = []

  operators.forEach((operator) => {
    let total = 0
    let matchedAll = true
    tokens.forEach((token) => {
      const score = tokenScore(operator.folded, token)
      if (Number.isFinite(score)) total += score
      else matchedAll = false
    })
    if (matchedAll) hits.push({ operator, score: total })
  })

  hits.sort(
    (a, b) =>
      a.score - b.score ||
      b.operator.siteCount - a.operator.siteCount ||
      a.operator.name.localeCompare(b.operator.name),
  )
  return hits.slice(0, limit).map((hit) => hit.operator)
}

/** The spelling to show for a key the filter holds, or the key itself. */
export const untrackedOperatorLabel = (
  operators: readonly UntrackedOperator[],
  key: string,
): string => operators.find((operator) => operator.key === key)?.name ?? key
